import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { animate, stagger } from "animejs";
import { DateTime } from "luxon";
import {
  Ban,
  Cake,
  CalendarCheck,
  CalendarSearch,
  CalendarX2,
  ChevronDown,
  ChevronRight,
  FileText,
  MapPin,
  Send,
  Stethoscope,
  Tag,
  Undo2,
  User,
  X,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { withAppTimeZone } from "@/lib/timezone";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import {
  birthdayAgeOnDate,
  isBirthdayOnDate,
  isNewPatient,
  newPatientPhoneFromEvent,
} from "@/components/pages/calendar/calendar-utils";
import { AppointmentConfirmationDialog } from "@/components/pages/calendar/appointment-confirmation-dialog";
import { requestOpenCalendarAppointment } from "@/lib/calendar/calendar-navigation";
import { useHasPermission } from "@/hooks/use-permissions";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  cancelAdminUserAppointment,
  fetchAdminUserAppointments,
  setAdminAppointmentConfirmation,
} from "@/lib/api/admin";
import { fetchDoctorDetails } from "@/lib/api/users";
import { isWhatsAppBridgeStubName } from "@/lib/messages/messages-helpers";
import { normalizeAppointmentChangePayload } from "@/lib/realtime/appointment-realtime-helpers";
import { subscribeAppointmentEvents } from "@/lib/realtime/chat-realtime-service";
import { subscribeUserRefresh } from "@/lib/realtime/user-realtime-bridge";
import { cn } from "@/lib/utils";

function dateToRfc3339(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  return value.toISOString();
}

function dayLabelForDate(date) {
  const target = DateTime.fromJSDate(date).startOf("day");
  const today = DateTime.now().startOf("day");
  const diff = Math.round(target.diff(today, "days").days);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 1 && diff <= 6) return target.toFormat("EEEE · MMM d");
  if (diff < -1 && diff >= -6) return `Last ${target.toFormat("EEEE")} · ${target.toFormat("MMM d")}`;
  if (target.year === DateTime.now().year) return target.toFormat("EEE · MMM d");
  return target.toFormat("EEE · MMM d, yyyy");
}

function formatTimeRange(startAt, endAt) {
  const start = new Date(startAt);
  const end = new Date(endAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "—";
  const fmt = (d) => d.toLocaleTimeString(undefined, withAppTimeZone({ hour: "2-digit", minute: "2-digit" }));
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatAppointmentDate(startAt) {
  const start = DateTime.fromJSDate(new Date(startAt));
  if (!start.isValid) return "—";
  return start.toFormat("MMM d, yyyy");
}

function getAppointmentStartMs(item) {
  const candidates = [item?.startAt, item?.raw?.startAt, item?.raw?.start, item?.raw?.startsAt];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    const t = new Date(c).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return Number.POSITIVE_INFINITY;
}

const STATUS_CHIP_STYLES = {
  confirmed:
    "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/60 dark:text-emerald-100",
  booked:
    "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/60 dark:text-emerald-100",
  scheduled:
    "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-100",
  pending:
    "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-100",
  cancelled:
    "border-rose-300 bg-rose-100 text-rose-900 line-through decoration-rose-700/40 dark:border-rose-500/40 dark:bg-rose-950/60 dark:text-rose-100",
  canceled:
    "border-rose-300 bg-rose-100 text-rose-900 line-through decoration-rose-700/40 dark:border-rose-500/40 dark:bg-rose-950/60 dark:text-rose-100",
  completed:
    "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600/40 dark:bg-slate-800/50 dark:text-slate-200",
  no_show:
    "border-rose-300 bg-rose-100 text-rose-900 dark:border-rose-500/40 dark:bg-rose-950/60 dark:text-rose-100",
};

function StatusChip({ status }) {
  const raw = String(status || "").trim().toLowerCase();
  if (!raw) return null;
  const cls = STATUS_CHIP_STYLES[raw] || "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600/40 dark:bg-slate-800/50 dark:text-slate-200";
  return (
    <span className={cn("inline-flex shrink-0 items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold leading-none", cls)}>
      {raw.replace(/_/g, " ")}
    </span>
  );
}

function isCancelledStatus(status) {
  const raw = String(status || "").trim().toLowerCase();
  return raw === "cancelled" || raw === "canceled" || raw === "no_show";
}

/** "#RRGGBB" (uppercased) or "" — mirrors the calendar's per-appointment color rule. */
function normalizeHex(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  const hex = s.startsWith("#") ? s.slice(1) : s;
  return /^[0-9a-fA-F]{6}$/.test(hex) ? `#${hex.toUpperCase()}` : "";
}

/** Attendance-confirmation state (metadata.confirmationStatus): "pending" | "confirmed" | "". */
function getConfirmationStatus(item) {
  const value = String(item?.raw?.metadata?.confirmationStatus || "").trim().toLowerCase();
  return value === "confirmed" || value === "pending" ? value : "";
}

/** Cancellation reason (metadata.cancellationReason) — the bot stamps the patient's WhatsApp reason here. */
function getCancellationReason(item) {
  return String(item?.raw?.metadata?.cancellationReason || "").trim();
}

function toTitleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Flatten an appointment `location` (string or object) into a single readable line. */
function formatLocationText(location) {
  if (!location) return "";
  if (typeof location === "string") return location.trim();
  if (typeof location !== "object") return "";
  const keys = ["displayName", "name", "label", "address", "clinic", "room", "unit", "city", "postalCode", "country"];
  const picked = keys.map((k) => String(location[k] ?? "").trim()).filter(Boolean);
  if (picked.length > 0) return [...new Set(picked)].join(", ");
  const first = Object.values(location).find((v) => typeof v === "string" && v.trim().length > 0);
  return String(first || "").trim();
}

/** Clinic label (tenant) as the headline, full address kept for the tooltip. */
function describeLocation(item) {
  const clinic = resolveTenantDisplayName(item?.raw?._tenant);
  const address = formatLocationText(item?.raw?.location);
  const label = clinic || address;
  const detail = clinic && address && address !== clinic ? address : "";
  return { label, detail };
}

/** Per-appointment color swatch (metadata.colorHex), matching the calendar card. */
function ColorDot({ hex }) {
  if (!hex) return null;
  return (
    <span
      className="size-2 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/15"
      style={{ backgroundColor: hex }}
      aria-hidden
    />
  );
}

/**
 * Attendance-confirmation cue: green calendar-check when the patient confirmed,
 * amber paper-plane when a confirmation was sent and we're awaiting their reply.
 * Same glyphs the calendar card uses so the two views read identically.
 */
function ConfirmationIcon({ status }) {
  if (status === "confirmed") {
    return (
      <span
        className="inline-flex shrink-0 items-center text-emerald-600 dark:text-emerald-300"
        title="Confirmed by patient"
        role="img"
        aria-label="Appointment confirmed"
      >
        <CalendarCheck className="size-3.5" strokeWidth={2.25} />
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span
        className="inline-flex shrink-0 items-center text-amber-600 dark:text-amber-400"
        title="Confirmation sent — awaiting the patient's reply"
        role="img"
        aria-label="Awaiting patient confirmation"
      >
        <Send className="size-3.5" strokeWidth={2.25} />
      </span>
    );
  }
  return null;
}

/** One-line meta row (location, reason, …) with a leading icon. */
function MetaRow({ icon: Icon, children, title }) {
  return (
    <p className="mt-1 flex min-w-0 items-start gap-1.5 text-[11px] text-muted-foreground" title={title}>
      <Icon className="mt-px size-3 shrink-0 opacity-80" aria-hidden />
      <span className="min-w-0 truncate">{children}</span>
    </p>
  );
}

function isPastEnd(endAt) {
  const end = new Date(endAt);
  if (Number.isNaN(end.getTime())) return false;
  return end.getTime() < Date.now();
}

const INITIAL_VISIBLE_PAST_APPOINTMENTS = 3;
const MAX_VISIBLE_PAST_APPOINTMENTS = 25;
const SHOW_MORE_PAST_STEP = 3;

function groupByDayAsc(items) {
  const map = new Map();
  for (const item of items) {
    const ms = getAppointmentStartMs(item);
    if (!Number.isFinite(ms)) continue;
    const date = new Date(ms);
    const key = DateTime.fromJSDate(date).toISODate();
    if (!map.has(key)) map.set(key, { dayKey: key, label: dayLabelForDate(date), items: [] });
    map.get(key).items.push(item);
  }
  return Array.from(map.values()).sort((a, b) => a.dayKey.localeCompare(b.dayKey));
}

function DayGroupHeader({ label, count }) {
  return (
    <div className="sticky top-0 z-[1] flex items-center justify-between border-b border-border/50 bg-background/85 px-1 pb-1 pt-0.5 backdrop-blur-sm">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {typeof count === "number" ? (
        <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold leading-none text-muted-foreground">
          {count}
        </span>
      ) : null}
    </div>
  );
}

function getDoctorPresentation(doctor) {
  if (!doctor) return { label: "", specialties: [] };
  const fullName = [doctor.firstName, doctor.lastName].filter(Boolean).join(" ").trim();
  const displayName = String(doctor.displayName || "").trim();
  const label = fullName || displayName || "";
  const specialties = Array.isArray(doctor.specialties)
    ? doctor.specialties.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  return { label, specialties };
}

/** Patient display when API embeds `_patient`; otherwise a thread-safe label (not raw user id). */
function resolveAppointmentPatientName(item, panelUserId, panelUserLabel) {
  const embedded = item?.raw?._patient;
  if (embedded && typeof embedded === "object") {
    const first = String(embedded.firstName || "").trim();
    const last = String(embedded.lastName || "").trim();
    const combined = `${first} ${last}`.trim();
    if (combined && !isWhatsAppBridgeStubName(combined)) return combined;
    const dn = String(embedded.displayName || "").trim();
    // An unmatched WhatsApp number's scheduling record echoes the bridge external-stub login id
    // ("ext-bot-…") as its name; skip it so we fall through to the resolved panel label (the
    // patient's WhatsApp name or phone) instead of showing the relay id.
    if (dn && !isWhatsAppBridgeStubName(dn)) return dn;
  }
  const uid = String(panelUserId || "").trim();
  const label = String(panelUserLabel || "").trim();
  if (label && label !== uid) return label;
  return "";
}

function SpecialtyChips({ specialties }) {
  if (!specialties?.length) return null;
  return (
    <span
      className="shrink-0 rounded-md border border-border/60 bg-muted/50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground"
      title={specialties.join(", ")}
    >
      {specialties[0]}
      {specialties.length > 1 ? ` +${specialties.length - 1}` : ""}
    </span>
  );
}

/** Birthday cake + New-patient pill shown right after the patient's name. */
function PatientCues({ birthday, birthdayAge, newPatient }) {
  if (!birthday && !newPatient) return null;
  return (
    <>
      {birthday ? (
        <span
          className="inline-flex shrink-0 items-center text-pink-500 dark:text-pink-300"
          title={birthdayAge != null ? `Birthday today — turns ${birthdayAge}! 🎉` : "Birthday today! 🎉"}
          role="img"
          aria-label="Patient has a birthday today"
        >
          <Cake className="size-3" strokeWidth={2.25} />
        </span>
      ) : null}
      {newPatient ? (
        <span className="shrink-0 rounded-sm bg-violet-500/15 px-1 py-px text-[0.5625rem] font-semibold uppercase leading-none tracking-wide text-violet-700 dark:bg-violet-400/15 dark:text-violet-200">
          New
        </span>
      ) : null}
    </>
  );
}

function AppointmentParticipants({ doctor, patientName, dim, birthday, birthdayAge, newPatient }) {
  const { label: doctorLabel, specialties } = getDoctorPresentation(doctor);
  const doctorPrimary = doctorLabel || "Doctor";
  const showPatient = Boolean(patientName);
  const showDoctor = Boolean(doctor);

  const muted = dim ? "text-muted-foreground/80" : "text-muted-foreground";

  if (!showPatient && !showDoctor) return null;

  if (showPatient && showDoctor) {
    return (
      <div className={cn("mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px]", muted)}>
        <span className="inline-flex min-w-0 max-w-full items-center gap-1">
          <User className="size-3 shrink-0 opacity-80" aria-hidden />
          <span
            className={cn(
              "min-w-0 truncate",
              dim ? "font-medium text-foreground/80" : "font-medium text-foreground/90",
            )}
            title={patientName}
          >
            {patientName}
          </span>
          <PatientCues birthday={birthday} birthdayAge={birthdayAge} newPatient={newPatient} />
        </span>
        <span className="select-none text-muted-foreground/45" aria-hidden>
          ·
        </span>
        <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
          <Stethoscope className="size-3 shrink-0" aria-hidden />
          <span className="min-w-0 truncate" title={doctorPrimary}>
            {doctorPrimary}
          </span>
          <SpecialtyChips specialties={specialties} />
        </span>
      </div>
    );
  }

  if (showDoctor) {
    return (
      <p className={cn("mt-1 flex min-w-0 items-center gap-1.5 text-[11px]", muted)}>
        <Stethoscope className="size-3 shrink-0" aria-hidden />
        <span className="min-w-0 truncate" title={doctorPrimary}>
          {doctorPrimary}
        </span>
        {specialties.length ? (
          <span className="ml-1 shrink-0">
            <SpecialtyChips specialties={specialties} />
          </span>
        ) : null}
      </p>
    );
  }

  return (
    <p className={cn("mt-1 flex min-w-0 items-center gap-1.5 text-[11px]", muted)}>
      <User className="size-3 shrink-0 opacity-80" aria-hidden />
      <span
        className={cn("min-w-0 truncate", dim ? "font-medium text-foreground/80" : "font-medium text-foreground/90")}
        title={patientName}
      >
        {patientName}
      </span>
      <PatientCues birthday={birthday} birthdayAge={birthdayAge} newPatient={newPatient} />
    </p>
  );
}

export function DirectUserAppointmentsPanel({ userId, userLabel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [doctorById, setDoctorById] = useState({});
  const [refreshTick, setRefreshTick] = useState(0);
  const [liveFlash, setLiveFlash] = useState(false);
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [confirmingAppointmentId, setConfirmingAppointmentId] = useState("");
  const [confirmError, setConfirmError] = useState("");
  // Target of the shared "review then send" confirmation dialog (null = closed).
  const [confirmationTarget, setConfirmationTarget] = useState(null);

  // Section collapse state — Upcoming open, Past collapsed by default.
  const [upcomingCollapsed, setUpcomingCollapsed] = useState(false);
  const [pastCollapsed, setPastCollapsed] = useState(true);
  const [pastVisibleLimit, setPastVisibleLimit] = useState(INITIAL_VISIBLE_PAST_APPOINTMENTS);

  const listRef = useRef(null);
  const isRealtimeUpdate = useRef(false);

  // Double-click reveals the visit on the Calendar page — pointless (and bounced back to
  // Dashboard by App's RBAC gate) for someone who can't open that page at all.
  const canOpenInCalendar = useHasPermission("calendar", "view");

  const normalizedUserId = String(userId || "").trim();
  const normalizedUserLabel = String(userLabel || "").trim() || normalizedUserId;

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const diff = getAppointmentStartMs(a) - getAppointmentStartMs(b);
        if (diff !== 0) return diff;
        const idA = String(a?.id || "");
        const idB = String(b?.id || "");
        return idA.localeCompare(idB);
      }),
    [items],
  );

  const { upcoming, past } = useMemo(() => {
    const u = [];
    const p = [];
    for (const it of sortedItems) {
      if (isPastEnd(it.endAt)) p.push(it);
      else u.push(it);
    }
    // Past — most recent first.
    p.sort((a, b) => getAppointmentStartMs(b) - getAppointmentStartMs(a));
    return { upcoming: u, past: p };
  }, [sortedItems]);

  const cappedPastCount = Math.min(past.length, MAX_VISIBLE_PAST_APPOINTMENTS);
  const visiblePastCount = Math.min(pastVisibleLimit, cappedPastCount);
  const visiblePast = useMemo(() => past.slice(0, visiblePastCount), [past, visiblePastCount]);
  const hiddenPastCount = Math.max(0, cappedPastCount - visiblePast.length);
  const hardLimitHiddenPastCount = Math.max(0, past.length - MAX_VISIBLE_PAST_APPOINTMENTS);

  const upcomingGroups = useMemo(() => groupByDayAsc(upcoming), [upcoming]);
  useEffect(() => {
    if (!normalizedUserId) return () => {};
    return subscribeAppointmentEvents((payload) => {
      const normalized = normalizeAppointmentChangePayload(payload);
      if (!normalized) return;
      const eventPatientId = String(normalized.patientId || "").trim();
      if (!eventPatientId || eventPatientId === normalizedUserId) {
        isRealtimeUpdate.current = true;
        setLiveFlash(true);
        setRefreshTick((prev) => prev + 1);
      }
    });
  }, [normalizedUserId]);

  useEffect(() => {
    if (!normalizedUserId) return () => {};
    return subscribeUserRefresh(({ change }) => {
      const eventUserId = String(change?.userId || "").trim();
      if (eventUserId && eventUserId !== normalizedUserId) {
        return;
      }
      isRealtimeUpdate.current = true;
      setLiveFlash(true);
      setRefreshTick((prev) => prev + 1);
    });
  }, [normalizedUserId]);

  useEffect(() => {
    if (!liveFlash) return;
    const timer = setTimeout(() => setLiveFlash(false), 3000);
    return () => clearTimeout(timer);
  }, [liveFlash]);

  useEffect(() => {
    if (!normalizedUserId) {
      setItems([]);
      setDoctorById({});
      setError("");
      setLoading(false);
      setPastVisibleLimit(INITIAL_VISIBLE_PAST_APPOINTMENTS);
      return;
    }

    let cancelled = false;
    const now = DateTime.now();
    const from = now.minus({ months: 12 }).toJSDate();
    const to = now.plus({ days: 365 }).toJSDate();

    setLoading(true);
    setError("");
    fetchAdminUserAppointments({
      userId: normalizedUserId,
      from: dateToRfc3339(from),
      to: dateToRfc3339(to),
      includePast: true,
      // Include cancelled visits so the bot's cancellations — and the reason
      // the patient gave — show here instead of silently disappearing.
      includeCancelled: true,
      limit: 200,
      skip: 0,
    })
      .then(async (response) => {
        if (cancelled) return;
        const list = Array.isArray(response?.items) ? response.items : [];
        setItems(list);

        const doctorIds = [
          ...new Set(list.map((item) => String(item?.doctorId || "").trim()).values()),
        ].filter(Boolean);
        if (doctorIds.length === 0) {
          setDoctorById({});
          return;
        }

        const details = await Promise.all(
          doctorIds.map(async (doctorId) => {
            try {
              const res = await fetchDoctorDetails({ doctorId });
              return [doctorId, res?.doctor ?? null];
            } catch {
              return [doctorId, null];
            }
          }),
        );
        if (cancelled) return;
        setDoctorById(Object.fromEntries(details));
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setDoctorById({});
        setError(err instanceof Error ? err.message : "Could not load appointments");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [normalizedUserId, refreshTick]);

  useEffect(() => {
    setPastVisibleLimit(INITIAL_VISIBLE_PAST_APPOINTMENTS);
  }, [normalizedUserId]);

  useLayoutEffect(() => {
    if (!listRef.current || loading) return;
    const cards = listRef.current.querySelectorAll("[data-appt-card]");
    if (cards.length === 0) return;

    const isRealtime = isRealtimeUpdate.current;
    isRealtimeUpdate.current = false;

    if (isRealtime) {
      animate(cards, {
        translateY: [22, 0],
        opacity: [0, 1],
        scale: [0.94, 1],
        duration: 600,
        delay: stagger(45),
        ease: "outBack",
      });
    } else {
      animate(cards, {
        translateY: [10, 0],
        opacity: [0, 1],
        duration: 360,
        delay: stagger(28),
        ease: "outCubic",
      });
    }
  }, [upcomingGroups, visiblePast, loading, upcomingCollapsed, pastCollapsed]);

  if (!normalizedUserId) return null;

  // Manually stamp the local confirmation state (green check / clear), mirroring
  // the calendar card's "Mark confirmed" toggle — status "confirmed" is a manual
  // mark, "" clears it. Reloads via the same refreshTick the cancel button uses.
  const applyConfirmation = async (apptKey, appointmentId, nextStatus) => {
    const aid = String(appointmentId || "").trim();
    if (!aid) return;
    setConfirmingAppointmentId(apptKey);
    setConfirmError("");
    try {
      await setAdminAppointmentConfirmation({
        userId: normalizedUserId,
        appointmentId: aid,
        status: nextStatus,
        via: nextStatus === "confirmed" ? "manual" : undefined,
      });
      setRefreshTick((t) => t + 1);
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : "Could not update confirmation");
    } finally {
      setConfirmingAppointmentId("");
    }
  };

  const renderCard = (item, { dim, includeDate = false }) => {
    const apptKey = item.id || `${item.startAt}-${item.endAt}`;
    const isCancelling = cancellingAppointmentId === apptKey;
    const isConfirming = confirmingAppointmentId === apptKey;
    const cancelled = isCancelledStatus(item.status);
    // Direct entries on standalone admin calendars (metadata.directEntry) are not
    // visits — nothing to confirm attendance for.
    const directEntry = item?.raw?.metadata?.directEntry === true;
    const canCancel = !dim && !cancelled && Boolean(item.id);
    const canConfirm = !dim && !cancelled && Boolean(item.id) && !directEntry;
    // Same gate as the manual mark — an upcoming, live visit we can still message about.
    const canSendConfirmation = canConfirm;
    const canReveal = canOpenInCalendar && Boolean(item.id);
    const hasActions = canConfirm || canCancel || canSendConfirmation || canReveal;
    const doctor = doctorById[item.doctorId] || item?.raw?._doctor || null;
    const patientName = resolveAppointmentPatientName(item, normalizedUserId, normalizedUserLabel);

    // Calendar-parity enrichment — surfaced on upcoming (non-dim) cards only;
    // past visits stay compact. Reads straight off the raw calendarViewItem.
    const enriched = !dim;
    const colorHex = enriched ? normalizeHex(item?.raw?.metadata?.colorHex) : "";
    const confirmation = enriched ? getConfirmationStatus(item) : "";
    // The cancellation reason shows on any cancelled card (including dim past
    // ones) — it's the whole point of surfacing cancelled visits here.
    const cancellationReason = cancelled ? getCancellationReason(item) : "";
    const apptType = enriched ? String(item?.raw?.type || "").trim() : "";
    const reason = enriched ? String(item?.raw?.description || "").trim() : "";
    const { label: locationLabel, detail: locationDetail } = enriched
      ? describeLocation(item)
      : { label: "", detail: "" };
    const dob = item?.raw?._patient?.dob;
    const startDt = DateTime.fromJSDate(new Date(item.startAt));
    const birthday = enriched && isBirthdayOnDate(dob, startDt);
    const birthdayAge = birthday ? birthdayAgeOnDate(dob, startDt) : null;
    const newPatient = enriched && isNewPatient(item?.raw);

    // → Calendar, day view, this date, scrolled to and highlighting this visit. Reached
    // from the toolbar button and from double-clicking the card itself.
    const revealInCalendar = () =>
      requestOpenCalendarAppointment({ appointmentId: item.id, startAt: item.startAt });

    // The hover toolbar lives inside this <li>, so double-clicks on it never navigate.
    const handleCardDoubleClick = (event) => {
      if (event.target instanceof Element && event.target.closest("button, a, [role='button']")) {
        return;
      }
      // A double-click selects the card's text; drop it so the jump doesn't leave a
      // stray blue smear behind on the panel.
      window.getSelection?.()?.removeAllRanges();
      revealInCalendar();
    };

    return (
      <li
        key={apptKey}
        data-appt-card
        onDoubleClick={canReveal ? handleCardDoubleClick : undefined}
        title={canReveal ? "Double-click to show on the calendar" : undefined}
        className={cn(
          "group relative rounded-md border px-2.5 py-2 text-xs transition-[border-color,background-color,box-shadow] duration-200",
          canReveal ? "cursor-pointer" : "",
          dim
            ? "border-border/40 bg-muted/20 opacity-90 hover:bg-muted/30"
            : cancelled
              ? "border-rose-300/40 bg-rose-500/5 hover:border-rose-300/60 hover:bg-rose-500/10"
              : "border-primary/20 bg-primary/5 hover:border-primary/35 hover:bg-primary/10",
        )}
      >
        <div className="min-w-0">
          {includeDate ? (
            <div
              className={cn(
                "flex w-full items-center justify-between gap-2 text-xs",
                dim ? "text-muted-foreground" : "text-foreground",
                cancelled ? "line-through decoration-rose-500/60" : "",
              )}
            >
              <p className="min-w-0 truncate">{formatAppointmentDate(item.startAt)}</p>
              {/* Same corner as the hover toolbar — fade out so the two don't overlap. */}
              <p
                className={cn(
                  "shrink-0 font-mono font-semibold tabular-nums transition-opacity",
                  hasActions ? "group-hover:opacity-0" : "",
                )}
              >
                {formatTimeRange(item.startAt, item.endAt)}
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <ColorDot hex={colorHex} />
              <p
                className={cn(
                  "min-w-0 font-mono text-sm font-semibold tabular-nums",
                  cancelled ? "text-muted-foreground line-through decoration-rose-500/60" : "text-foreground",
                )}
              >
                {formatTimeRange(item.startAt, item.endAt)}
              </p>
              {/* On hover the floating action toolbar takes this corner, so
                  fade the status cue out to avoid the two overlapping. */}
              <span
                className={cn(
                  "ml-auto flex shrink-0 items-center gap-1.5 transition-opacity",
                  hasActions ? "group-hover:opacity-0" : "",
                )}
              >
                <ConfirmationIcon status={confirmation} />
                <StatusChip status={item.status} />
              </span>
            </div>
          )}
          {apptType ? (
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-muted/50 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                <Tag className="size-2.5 opacity-80" aria-hidden />
                {toTitleCase(apptType)}
              </span>
            </div>
          ) : null}
          <AppointmentParticipants
            doctor={doctor}
            patientName={patientName}
            dim={dim}
            birthday={birthday}
            birthdayAge={birthdayAge}
            newPatient={newPatient}
          />
          {locationLabel ? (
            <MetaRow icon={MapPin} title={[locationLabel, locationDetail].filter(Boolean).join(" — ")}>
              {locationLabel}
            </MetaRow>
          ) : null}
          {reason ? (
            <MetaRow icon={FileText} title={reason}>
              {reason}
            </MetaRow>
          ) : null}
          {cancellationReason ? (
            <p
              className="mt-1.5 flex min-w-0 items-start gap-1.5 rounded-md border border-rose-300/40 bg-rose-500/5 px-1.5 py-1 text-[11px] text-rose-700 dark:text-rose-200"
              title={cancellationReason}
            >
              <Ban className="mt-px size-3 shrink-0 opacity-80" aria-hidden />
              <span className="min-w-0 line-clamp-2 wrap-break-word">
                {cancellationReason}
              </span>
            </p>
          ) : null}
        </div>
        {/* Floating hover actions — overlaid in the top-right corner so they
            reserve no layout width (the card content stays full-width). */}
        {hasActions ? (
          <div className="pointer-events-none absolute right-1 top-1 z-10 flex items-center gap-0.5 rounded-md border border-border/60 bg-card/95 px-1 py-0.5 opacity-0 shadow-sm backdrop-blur-sm transition-opacity duration-100 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
            {canReveal ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    aria-label="Show this appointment on the calendar"
                    onClick={revealInCalendar}
                  >
                    <CalendarSearch className="size-3.5" strokeWidth={2.5} aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-xs">
                  Show on calendar
                </TooltipContent>
              </Tooltip>
            ) : null}
            {canSendConfirmation ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-amber-500/10 hover:text-amber-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:hover:text-amber-300"
                    aria-label="Send appointment confirmation"
                    onClick={() =>
                      setConfirmationTarget({
                        appointmentId: item.id,
                        patientName,
                        phone: newPatientPhoneFromEvent(item?.raw),
                      })
                    }
                  >
                    <Send className="size-3.5" strokeWidth={2.5} aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-xs">
                  Send confirmation…
                </TooltipContent>
              </Tooltip>
            ) : null}
            {canConfirm ? (
              confirmation === "confirmed" ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex size-6 shrink-0 items-center justify-center rounded text-emerald-600 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:text-emerald-300"
                      disabled={isConfirming}
                      aria-busy={isConfirming}
                      aria-label="Clear appointment confirmation"
                      onClick={() => applyConfirmation(apptKey, item.id, "")}
                    >
                      <Undo2 className="size-3.5" strokeWidth={2.5} aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="text-xs">
                    {isConfirming ? "Saving…" : "Clear confirmation"}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-emerald-500/10 hover:text-emerald-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:hover:text-emerald-300"
                      disabled={isConfirming}
                      aria-busy={isConfirming}
                      aria-label="Mark appointment confirmed"
                      onClick={() => applyConfirmation(apptKey, item.id, "confirmed")}
                    >
                      <CalendarCheck className="size-3.5" strokeWidth={2.5} aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="text-xs">
                    {isConfirming ? "Saving…" : "Mark as confirmed"}
                  </TooltipContent>
                </Tooltip>
              )
            ) : null}
            {canCancel ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    disabled={isCancelling}
                    aria-busy={isCancelling}
                    aria-label="Cancel appointment"
                    onClick={async () => {
                      const appointmentId = String(item?.id || "").trim();
                      if (!appointmentId) return;
                      if (!window.confirm("Cancel this appointment?")) return;
                      setCancellingAppointmentId(apptKey);
                      setCancelError("");
                      try {
                        await cancelAdminUserAppointment({
                          userId: normalizedUserId,
                          appointmentId,
                        });
                        setRefreshTick((t) => t + 1);
                      } catch (err) {
                        setCancelError(err instanceof Error ? err.message : "Could not cancel appointment");
                      } finally {
                        setCancellingAppointmentId("");
                      }
                    }}
                  >
                    <X className="size-3.5" strokeWidth={2.5} aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="text-xs">
                  {isCancelling ? "Cancelling…" : "Cancel appointment"}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        ) : null}
      </li>
    );
  };

  const renderGroups = (groups, { dim }) =>
    groups.map((group) => (
      <div key={group.dayKey} className="space-y-1">
        <DayGroupHeader label={group.label} count={group.items.length} />
        <ul className="space-y-1.5">{group.items.map((item) => renderCard(item, { dim }))}</ul>
      </div>
    ));

  const totalCount = upcoming.length + visiblePast.length;

  return (
    <TooltipProvider delayDuration={300}>
      <section
        className={cn(
          "relative space-y-2 rounded-lg border bg-background/60 p-3 transition-all duration-700 ease-out",
          liveFlash
            ? "border-blue-400/50 shadow-[0_0_18px_2px_rgba(96,165,250,0.10)]"
            : "border-border/60 shadow-none",
        )}
      >
        <div className="flex items-center gap-2">
          <div className="flex-1 space-y-0.5">
            <div className="flex items-center gap-1.5">
              <h4 className="text-xs font-semibold text-foreground">Appointments</h4>
              {liveFlash ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                  <span className="h-1.5 w-1.5 animate-ping rounded-full bg-emerald-500" />
                  Updated
                </span>
              ) : null}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Showing visits from the last 12 months onward.
            </p>
          </div>
        </div>

        {cancelError ? (
          <p className="rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-[11px] text-destructive">
            {cancelError}
          </p>
        ) : null}

        {confirmError ? (
          <p className="rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-[11px] text-destructive">
            {confirmError}
          </p>
        ) : null}

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-3 w-20 rounded" />
            <Skeleton className="h-12 w-full rounded-md" />
            <Skeleton className="h-12 w-full rounded-md" />
          </div>
        ) : error ? (
          <p className="text-xs text-destructive">{error}</p>
        ) : totalCount === 0 ? (
          <div className="flex flex-col items-center gap-1.5 rounded-md border border-dashed border-border/60 bg-background/40 px-3 py-6 text-center">
            <CalendarX2 className="h-5 w-5 text-muted-foreground/70" aria-hidden />
            <p className="text-[11px] font-medium text-foreground">No appointments</p>
            <p className="text-[10px] text-muted-foreground">
              {normalizedUserLabel} has no visits in this range.
            </p>
          </div>
        ) : (
          <div ref={listRef} className="space-y-3">
            {upcoming.length > 0 ? (
              <div className="space-y-2">
                <SectionToggle
                  label="Upcoming"
                  count={upcoming.length}
                  collapsed={upcomingCollapsed}
                  onToggle={() => setUpcomingCollapsed((v) => !v)}
                />
                {!upcomingCollapsed ? (
                  <div className="space-y-2">{renderGroups(upcomingGroups, { dim: false })}</div>
                ) : null}
              </div>
            ) : null}
            {past.length > 0 ? (
              <div className="space-y-2">
                <SectionToggle
                  label="Past"
                  collapsed={pastCollapsed}
                  onToggle={() => setPastCollapsed((v) => !v)}
                />
                {!pastCollapsed ? (
                  <div className="space-y-2">
                    {hiddenPastCount > 0 ? (
                      <p className="rounded-md border border-border/60 bg-muted/30 px-2 py-1 text-[10px] text-muted-foreground">
                        Showing {visiblePast.length} of {cappedPastCount} previous appointments.
                        {hardLimitHiddenPastCount > 0
                          ? ` ${hardLimitHiddenPastCount} older appointment${hardLimitHiddenPastCount === 1 ? "" : "s"} hidden (max 25).`
                          : ""}
                      </p>
                    ) : null}
                    <ul className="space-y-1.5">
                      {visiblePast.map((item) => renderCard(item, { dim: true, includeDate: true }))}
                    </ul>
                    {hiddenPastCount > 0 ? (
                      <button
                        type="button"
                        onClick={() =>
                          setPastVisibleLimit((prev) =>
                            Math.min(prev + SHOW_MORE_PAST_STEP, MAX_VISIBLE_PAST_APPOINTMENTS),
                          )
                        }
                        className="w-full rounded-md border border-border/60 bg-background px-2 py-1.5 text-[10px] font-semibold text-muted-foreground transition hover:bg-muted/40"
                      >
                        Show more ({hiddenPastCount} remaining)
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        {/* Shared review-then-send dialog — same one the calendar's "Send
            confirmation…" raises, so the draft and the clinic→number match
            behave identically here. */}
        <AppointmentConfirmationDialog
          open={confirmationTarget != null}
          onOpenChange={(next) => {
            if (!next) setConfirmationTarget(null);
          }}
          appointmentId={confirmationTarget?.appointmentId}
          patientUserId={normalizedUserId}
          patientName={confirmationTarget?.patientName || normalizedUserLabel}
          phone={confirmationTarget?.phone}
          // The calendar's hosts refresh off publishCalendarRefresh, which this
          // panel doesn't listen to — refetch so the card flips to the amber
          // "awaiting reply" cue right away.
          onSent={() => setRefreshTick((t) => t + 1)}
        />
      </section>
    </TooltipProvider>
  );
}

function SectionToggle({ label, count, collapsed, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      className="flex w-full items-center justify-between gap-2 rounded-md px-1 py-1 text-left transition hover:bg-muted/60"
    >
      <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {collapsed ? (
          <ChevronRight className="h-3 w-3 opacity-70" strokeWidth={2.5} aria-hidden />
        ) : (
          <ChevronDown className="h-3 w-3 opacity-70" strokeWidth={2.5} aria-hidden />
        )}
        {label}
      </span>
      {typeof count === "number" ? (
        <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold leading-none text-muted-foreground">
          {count}
        </span>
      ) : null}
    </button>
  );
}
